import axios from 'axios';

/**
 * Shared plumbing for the two analytics dashboards, so the views own layout
 * and panel wiring and nothing else.
 *
 * Every analytics endpoint takes the same body - { date: { start, end } } in
 * d/m/Y, matching AnalyticsRangeRequest's date_format:d/m/Y validation - and
 * is scoped server-side by the `location` header the store already carries.
 */

/** d/m/Y, e.g. 05/08/2026 - what the Kraken FormRequest validates. */
export function formatDMY(d) {
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/**
 * Body for an analytics request from a PrimeVue Calendar range value
 * ([start, end] where end is null until the second click).
 */
export function datePayload(dateRange) {
    const [start, end] = dateRange || [];
    return { date: { start: formatDMY(start), end: formatDMY(end || start) } };
}

/** Default window: the last 30 days, ending today. */
export function defaultRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);
    return [start, end];
}

export function fetchPanel(endpoint, dateRange, header) {
    return axios.post(endpoint, datePayload(dateRange), header).then((res) => res.data);
}

/**
 * Load panels one at a time, in the order given, rendering each as it lands.
 *
 * Sequential on purpose. The panels are independent, so firing all six at
 * once looks right - but two of the aggregates (unwitnessed-share,
 * drug-week-matrix) are an order of magnitude slower than the rest (~23 s
 * each on a local single-worker `artisan serve`, where concurrent requests
 * queue anyway). Batched rendering made the whole dashboard wait for the
 * slowest panel; sequential with the slow panels ORDERED LAST means the fast
 * five appear immediately and the stragglers fill in behind them.
 *
 * Each task: { key, endpoint, assign }. `loading[key]` is flipped for every
 * task up front (all cards show their spinner), then cleared as each panel
 * resolves. A failed panel is left empty and logged - one bad aggregate must
 * not blank the dashboard.
 *
 * `isStale()` guards re-entry: if the user changes the date range mid-run,
 * assignments from the superseded run are dropped rather than racing the new
 * one.
 */
export async function loadSequentially(tasks, dateRange, header, loading, isStale) {
    tasks.forEach((task) => {
        loading[task.key] = true;
    });

    for (const task of tasks) {
        try {
            const data = await fetchPanel(task.endpoint, dateRange, header);
            if (isStale()) return;
            task.assign(data);
        } catch (error) {
            if (isStale()) return;
            // eslint-disable-next-line no-console
            console.error(`Analytics panel ${task.key} failed`, error);
        } finally {
            if (!isStale()) loading[task.key] = false;
        }
    }
}
